import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { Notification } from "@/shared/types/notification";
import type { Shop } from "@/shared/types/shop";

/** Stato delle modali di sistema dello shell (spec 6.2): niente flag nello store, un context. */
export interface ShellDialogs {
  notificationsOpen: boolean;
  openNotifications: () => void;
  closeNotifications: () => void;
  notificationDetail: Notification | null;
  /** Apre il dettaglio e chiude la lista, come goToDetail nel legacy. */
  openNotificationDetail: (notification: Notification) => void;
  /** Chiude il dettaglio e riapre la lista, come il "Back" del legacy. */
  closeNotificationDetail: () => void;
  /** Chiude il dettaglio senza riaprire la lista ("View issue" dal dettaglio). */
  dismissNotificationDetail: () => void;
  /** Cambio shop con la guardia delle modifiche (fornito da AppFrame). */
  switchShop: (shop: Shop) => void;
}

const ShellDialogsContext = createContext<ShellDialogs | null>(null);

export function ShellDialogsProvider({
  switchShop,
  children,
}: {
  switchShop: (shop: Shop) => void;
  children: ReactNode;
}) {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationDetail, setNotificationDetail] = useState<Notification | null>(null);
  const openNotifications = useCallback(() => setNotificationsOpen(true), []);
  const closeNotifications = useCallback(() => setNotificationsOpen(false), []);
  const openNotificationDetail = useCallback((notification: Notification) => {
    setNotificationDetail(notification);
    setNotificationsOpen(false);
  }, []);
  const closeNotificationDetail = useCallback(() => {
    setNotificationDetail(null);
    setNotificationsOpen(true);
  }, []);
  const dismissNotificationDetail = useCallback(() => setNotificationDetail(null), []);
  const value = useMemo<ShellDialogs>(
    () => ({
      notificationsOpen,
      openNotifications,
      closeNotifications,
      notificationDetail,
      openNotificationDetail,
      closeNotificationDetail,
      dismissNotificationDetail,
      switchShop,
    }),
    [
      notificationsOpen,
      openNotifications,
      closeNotifications,
      notificationDetail,
      openNotificationDetail,
      closeNotificationDetail,
      dismissNotificationDetail,
      switchShop,
    ],
  );
  return <ShellDialogsContext.Provider value={value}>{children}</ShellDialogsContext.Provider>;
}

export function useShellDialogs(): ShellDialogs {
  const value = useContext(ShellDialogsContext);
  if (!value) throw new Error("useShellDialogs va usato dentro ShellDialogsProvider");
  return value;
}
